import React, { useState } from "react";
import UserCouponsTable from "./UserCouponsTable";
import axiosConfig from "../../../axios/axiosConfig";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MuiModal from "../../muiComps/MuiModal";
import CreateCouponForm from "./CreateCouponForm";
import ReactHotToaster from "../../common/ReactHotToaster";
import MuiTableSkeletonLoader from "../../muiComps/MuiTableSkeletonLoader";

const UserCouponCodesPage = () => {
  const {
    user: { userDetails },
  } = useSelector((store) => store.auth);
  const USERID = userDetails.id;

  const initialCouponDetailsInput = {
    coupon_name: "",
    coupon_desc: "",
    coupon_code: "",
    start_date: new Date().toLocaleDateString(),
    // expiry_date: new Date().toLocaleDateString(),
    expiry_date: "",
    discount_by: "percentage", // flat or percentage
    discount_value: "10",
    max_redemption: "1000",
    isHidden: false,
    banner_image: "",
    deeplink_url: "",
    home_screen_visible: false,
    broker_coupon: "no",
    broker_code: "",
  };

  const [openCreateCouponForm, setOpenCreateCouponForm] = useState(false); // toggle state for create coupon code form
  const [couponDetailsInput, setCouponDetailsInput] = useState(
    initialCouponDetailsInput
  );
  // const [isActiveInput, setIsActiveInput] = useState(false);
  const [isDeactivate, setIsDeactivate] = useState(false);
  const [userCouponsData, setUserCouponsData] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false); // Edit Mode toggle for CreateCouponForm
  const [activeUserCouponDetails, setActiveUserCouponDetails] = useState({}); // Only for Edit Mode

  const {
    coupon_name,
    coupon_desc,
    coupon_code,
    start_date,
    expiry_date,
    plan_associate_with,
    discount_by,
    discount_value,
    max_redemption,
    isHidden,
    banner_image,
    deeplink_url,
    home_screen_visible,
    broker_coupon,
    broker_code,
  } = couponDetailsInput;

  const userCouponsQuery = useQuery({
    queryKey: ["user-coupons"],
    queryFn: () => getUserCoupons(USERID),
    onSuccess: (data) => {
      setUserCouponsData(data.response);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  const formattedStartDate = start_date.replaceAll("/", "-");
  const formattedExpirtyDate = expiry_date.replaceAll("/", "-");

  // POST NEW COUPON
  const createCouponQuery = useMutation({
    // mutationFn: fetchAllRecommendedStocks(selectedSegmentTab),
    mutationFn: async () => {
      // if (!selectedSegmentTab) return;
      const formData = new FormData();
      formData.append("userid", USERID);
      formData.append("description", coupon_desc);
      formData.append("title", coupon_name);
      formData.append("coupon_code", coupon_code.toUpperCase());
      formData.append("discount", `${discount_value} ${discount_by}`);
      formData.append("coupon_type", "all");
      formData.append("plan_associate_with", plan_associate_with);
      formData.append("start_date", formattedStartDate);
      formData.append("expiry_date", formattedExpirtyDate);
      formData.append("active", true);
      formData.append("redemption", "one_time");
      formData.append("max_redemption", max_redemption);
      formData.append("is_hide", isHidden);
      formData.append("deeplink_url", deeplink_url);
      formData.append("home_screen_visible", home_screen_visible);
      formData.append("broker_coupon", broker_coupon === "yes");
      if (broker_coupon === "yes" && broker_code) {
        formData.append("broker_code", broker_code);
      }
      if (banner_image) {
        formData.append("banner_image", banner_image);
      }


      const res = await axiosConfig.post("/referral/coupon", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setUserCouponsData((prevState) => {
        return [...prevState, data.response];
      });
    },
    onError: (error) => {
      toast.error(error.message || "Something went wrong");
    },
  });

  // HANDLER FUNCTION FOR SUBMIT (CREATE) BUTTON (CREATE COUPON)
  const handleCouponSubmit = () => {
    createCouponQuery.mutate();
    setOpenCreateCouponForm(false);
    // setCouponDetailsInput(initialCouponDetailsInput);
  };

  // EDIT / PATCH COUPON FORM for activating or deactivating
  const coupon_id = activeUserCouponDetails.id;
  const editCouponQuery = useMutation({
    mutationFn: async () => {
      if (!coupon_id) return;

      // this function / API only activates or deactivates the coupon.
      const res = await axiosConfig({
        method: "patch",
        url: `/referral/coupon?coupon_id=${coupon_id}`,
        data: {
          userid: USERID,
          active: !isDeactivate, // isDeactivate is opposite of active
          is_hide: isHidden,
          description: coupon_desc,
          title: coupon_name,
          expiry_date: formattedExpirtyDate,
          max_redemption: max_redemption,
          home_screen_visible:home_screen_visible
        },
      });
      return res.data;  
    },
    onSuccess: (data) => {
      const newUserCouponsData = userCouponsData.map((item) => {
        if (item.id === coupon_id) {
          return data.response;
        }

        return item;
      });
      setUserCouponsData(newUserCouponsData);
    },
    onError: (error) => {
      toast.error(error.message || "Something went wrong");
    },
  });

  // HANDLER FUNCTION FOR SAVING BUTTON IN COUPON FORM
  const handleCouponEditSave = () => {
    editCouponQuery.mutate();
    setOpenCreateCouponForm(false);
    // setCouponDetailsInput(initialCouponDetailsInput);
  };

  // HANDLER FUNCTION FOR EDITING USER COUPON BUTTON IN TABLE ROW
  const handleEditCoupon = (user_details) => {
    const {
      title,
      description,
      coupon_code,
      discount,
      max_redemption,
      start_date: startDate,
      expiry_date: expiryDate,
    } = user_details;
    setIsEditMode(true);
    setActiveUserCouponDetails(user_details);
    setOpenCreateCouponForm(true);
    setCouponDetailsInput({
      coupon_name: title,
      coupon_desc: description,
      coupon_code: coupon_code.toUpperCase(),
      plan_associate_with: plan_associate_with,
      discount_value: discount.split(" ")[0],
      discount_type: discount.split(" ")[1],
      start_date: startDate,
      expiry_date: expiryDate,
      max_redemption: max_redemption,
    });
  };

  // CREATE NEW COUPON BUTTON HANDLER
  const handleCreateNewCoupon = () => {
    setOpenCreateCouponForm(true);
    setIsEditMode(false);
    setCouponDetailsInput(initialCouponDetailsInput);
  };

  const couponID = activeUserCouponDetails?.id;
  // DELETE COUPON
  const deleteCouponQuery = useMutation({
    mutationFn: async () => {
      if (!couponID) return;

      const res = await axiosConfig({
        method: "delete",
        url: `/referral/coupon?coupon_id=${couponID}`,
        data: {},
      });
      return res.data;
    },
    onSuccess: (data) => {
      setUserCouponsData((prevState) => {
        return prevState.filter((item) => {
          return item.id !== couponID;
        });
      });
      toast.success("Coupon deleted successfully!");
    },

    onError: (error) => {
      toast.error(error.message || "Something went wrong");
    },
  });

  const handleDeleteCoupon = (user_details) => {
    const consent = window.confirm(
      "Are you sure you want to delete this coupon?"
    );

    if (!consent) {
      return;
    }
    setActiveUserCouponDetails(user_details);
    deleteCouponQuery.mutate();
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <LocalOfferIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Coupon Codes
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              Manage and track all coupon codes
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleCreateNewCoupon}
          sx={{
            background: "linear-gradient(135deg, #016551, #014d3d)",
            color: "#fff",
            fontWeight: 600,
            borderRadius: "8px",
            px: 3,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
          }}
        >
          Create New Coupon
        </Button>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        {userCouponsQuery.isLoading ? (
          <MuiTableSkeletonLoader />
        ) : (
          <UserCouponsTable
            userCouponsData={userCouponsData}
            handleEditCoupon={handleEditCoupon}
            handleDeleteCoupon={handleDeleteCoupon}
          />
        )}
      </Box>

      {/* CREATE NEW COUPON FORM */}
      <MuiModal open={openCreateCouponForm} setOpen={setOpenCreateCouponForm}>
        <CreateCouponForm
          couponDetailsInput={couponDetailsInput}
          setCouponDetailsInput={setCouponDetailsInput}
          isDeactivate={isDeactivate}
          setIsDeactivate={setIsDeactivate}
          handleCouponSubmit={handleCouponSubmit}
          setOpenCreateCouponForm={setOpenCreateCouponForm}
          isEditMode={isEditMode}
          handleCouponEditSave={handleCouponEditSave}
        />
      </MuiModal>

      {/* REACT HOT TOAST */}
      <ReactHotToaster />
    </Box>
  );
};

export default UserCouponCodesPage;

// UTLILITY / API FUNCTIONS
const getUserCoupons = async (admin_user_id) => {
  if (!admin_user_id) {
    return;
  }

  const res = await axiosConfig({
    method: "get",
    url: `/referral/coupon?userid=${admin_user_id}`,
    data: {},
  });

  return res.data;
};
